import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import {
  CheckCircle2,
  ClipboardList,
  FolderSearch,
} from "lucide-react";
import { CalidadControlModificacionDeleteModal } from "../components/control-modificaciones/CalidadControlModificacionDeleteModal";
import { CalidadControlModificacionFormModal } from "../components/control-modificaciones/CalidadControlModificacionFormModal";
import { CalidadControlModificacionesTable } from "../components/control-modificaciones/CalidadControlModificacionesTable";
import {
  createCalidadIncidenciaControlModificacion,
  createCalidadIncidencia,
  deleteCalidadIncidenciaControlModificacion,
  deleteCalidadIncidencia,
  updateCalidadIncidenciaControlModificacion,
  updateCalidadIncidencia,
  updateCalidadNcpFicha,
} from "../sections/incidencias/api/calidad";
import { Iso900114001Hub } from "../components/Iso900114001Hub";
import { Iso900114001OverviewCard } from "../components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../components/shared/Iso900114001Tabs";
import { CertificationStandardHero } from "../../../shared/ui";
import { useCanManageCertificaciones } from "../../../shared/permissions/useCertificationPermissions";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import { CalidadConfirmDeleteModal } from "../sections/incidencias/components/CalidadConfirmDeleteModal";
import { CalidadIncidenciaFormModal } from "../sections/incidencias/components/CalidadIncidenciaFormModal";
import { CalidadIncidenciasTable } from "../sections/incidencias/components/CalidadIncidenciasTable";
import { CalidadNcpFichaView } from "../sections/incidencias/components/CalidadNcpFichaView";
import { CalidadSuministradoresSection } from "../sections/suministradores-homologados/components/CalidadSuministradoresSection";
import { CalidadRequisitosLegalesSection } from "../sections/requisitos-legales/components/CalidadRequisitosLegalesSection";
import { CalidadObjetivosSection } from "../sections/objetivos/components/CalidadObjetivosSection";
import { CalidadPlanificacionCambiosSection } from "../sections/planificacion-de-cambios/components/CalidadPlanificacionCambiosSection";
import { CalidadRiesgosOportunidadesSection } from "../sections/riesgos-oportunidades/components/CalidadRiesgosOportunidadesSection";
import { CalidadDafoSection } from "../sections/dafo/components/CalidadDafoSection";
import { CalidadMantenimientoVehiculosSection } from "../sections/mantenimiento-vehiculos/components/CalidadMantenimientoVehiculosSection";
import { CalidadMantenimientoInfraestructurasSection } from "../sections/mantenimiento-infraestructuras/components/CalidadMantenimientoInfraestructurasSection";
import { CalidadControlOperacionalSection } from "../sections/control-operacional/components/CalidadControlOperacionalSection";
import { CalidadFormacionSection } from "../sections/formacion/components/CalidadFormacionSection";
import { ManualSistemaIntegradoSection } from "../../../shared/manual-sistema-integrado/components/ManualSistemaIntegradoSection";
import { IndicadoresSection } from "../../../shared/indicadores/components/IndicadoresSection";
import { IsoCertificateActions } from "../../../shared/certificados-iso/components/IsoCertificateActions";
import { IsoCertificatePreviewModal } from "../../../shared/certificados-iso/components/IsoCertificatePreviewModal";
import { IsoCertificateUploadModal } from "../../../shared/certificados-iso/components/IsoCertificateUploadModal";
import { useIsoCertificates } from "../../../shared/certificados-iso/hooks/useIsoCertificates";
import type { IsoCertificateSlot } from "../../../shared/certificados-iso/types/certificados-iso";
import { useCalidadIncidencias } from "../sections/incidencias/hooks/useCalidadIncidencias";
import { useCalidadNcpFicha } from "../sections/incidencias/hooks/useCalidadNcpFicha";
import {
  createEmptyCalidadIncidenciaForm,
  defaultCalidadFilters,
  mapFichaToPayload,
  mapFormToIncidenciaPayload,
  mapIncidenciaToForm,
  type CalidadIncidencia,
  type CalidadIncidenciaFormValues,
} from "../sections/incidencias/types/calidad";
import type {
  CalidadControlModificacion,
  CalidadControlModificacionPayload,
} from "../types/control-modificaciones";
import "../../../styles/certificaciones.css";
import "../styles/iso-9001-14001.css";
import "../sections/incidencias/styles/incidencias.css";
import "../sections/suministradores-homologados/styles/suministradores-homologados.css";
import "../sections/requisitos-legales/styles/requisitos-legales.css";
import "../sections/objetivos/styles/objetivos.css";
import "../sections/planificacion-de-cambios/styles/planificacion-de-cambios.css";
import "../sections/riesgos-oportunidades/styles/riesgos-oportunidades.css";
import "../sections/dafo/styles/dafo.css";
import "../styles/operational-sections.css";
import "../../iso-27001-27701/styles/iso-27001-27701.css";

type Iso900114001Section =
  | "hub"
  | "manual-sistema-integrado"
  | "indicadores"
  | "incidencias"
  | "ficha"
  | "suministradores"
  | "requisitos-legales"
  | "objetivos"
  | "dafo"
  | "planificacion-de-cambios"
  | "riesgos-oportunidades"
  | "mantenimiento-vehiculos"
  | "mantenimiento-infraestructuras"
  | "control-operacional"
  | "formacion";
const ISO_9001_14001_SECTION_LABELS: Record<Iso900114001Section, string> = {
  hub: "Hub",
  "manual-sistema-integrado": "Manual del Sistema Integrado",
  indicadores: "Indicadores",
  incidencias: "Incidencias",
  ficha: "Ficha NCp",
  suministradores: "Suministradores",
  "requisitos-legales": "Requisitos legales",
  objetivos: "Objetivos",
  dafo: "DAFO",
  "planificacion-de-cambios": "Planificación",
  "riesgos-oportunidades": "Riesgos y oportunidades",
  "mantenimiento-vehiculos": "Mantenimiento de vehículos",
  "mantenimiento-infraestructuras": "Mantenimiento de infraestructuras",
  "control-operacional": "Control operacional",
  formacion: "Formación",
};

type FormMode = "create" | "edit";
type ControlFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: CalidadControlModificacion | null;
    };

type ControlDeleteState =
  | { open: false }
  | {
      open: true;
      id: number;
      title: string;
      description: string;
    };

export function Iso900114001Page() {
  const canManageCertificaciones = useCanManageCertificaciones();
  const navigate = useNavigate();
  const certificates = useIsoCertificates();
  const [previewCertificate, setPreviewCertificate] = useState<IsoCertificateSlot | null>(null);
  const [uploadCertificate, setUploadCertificate] = useState<IsoCertificateSlot | null>(null);
  const [activeSection, setActiveSection] = useState<Iso900114001Section>("hub");
  const [incidenciasTab, setIncidenciasTab] = useState<"incidencias" | "control">("incidencias");
  const [filters, setFilters] = useState(defaultCalidadFilters);
  const [page, setPage] = useState(1);
  const [formMode, setFormMode] = useState<FormMode>("create");
  const [formOpen, setFormOpen] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [controlFormState, setControlFormState] = useState<ControlFormState>({
    open: false,
  });
  const [controlFormSubmitting, setControlFormSubmitting] = useState(false);
  const [controlFormError, setControlFormError] = useState<string | null>(null);
  const [controlDeleteState, setControlDeleteState] = useState<ControlDeleteState>({
    open: false,
  });
  const [controlDeleteSubmitting, setControlDeleteSubmitting] = useState(false);
  const [editingIncidencia, setEditingIncidencia] = useState<CalidadIncidencia | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CalidadIncidencia | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [selectedIncidenciaId, setSelectedIncidenciaId] = useState<number | null>(null);
  const [fichaSaving, setFichaSaving] = useState(false);
  const [fichaSaveError, setFichaSaveError] = useState<string | null>(null);

  useGlobalNavigationTarget("cert-iso-9001-14001", (target) => {
    switch (target.section) {
      case "hub":
      case "manual-sistema-integrado":
      case "indicadores":
      case "incidencias":
      case "suministradores":
      case "requisitos-legales":
      case "objetivos":
      case "dafo":
      case "planificacion-de-cambios":
      case "riesgos-oportunidades":
      case "mantenimiento-vehiculos":
      case "mantenimiento-infraestructuras":
      case "control-operacional":
      case "formacion":
        setActiveSection(target.section);
        break;
      default:
        break;
    }

    if (target.section === "incidencias") {
      setPage(1);
      setIncidenciasTab(target.subsection === "control" ? "control" : "incidencias");
    }
  });

  const incidenciasEnabled =
    activeSection === "incidencias" || activeSection === "ficha";

  const {
    incidencias,
    controlModificaciones,
    meta,
    loading,
    error,
    reload,
  } = useCalidadIncidencias(filters, page, incidenciasEnabled);

  const {
    ficha,
    loading: fichaLoading,
    error: fichaError,
    reload: reloadFicha,
    setFicha,
  } = useCalidadNcpFicha(
    selectedIncidenciaId,
    activeSection === "ficha" && selectedIncidenciaId !== null,
  );

  const openManual = () => setActiveSection("manual-sistema-integrado");
  const openIndicadores = () => setActiveSection("indicadores");

  const openIncidencias = () => {
    setPage(1);
    setIncidenciasTab("incidencias");
    setActiveSection("incidencias");
  };

  const backToHub = () => {
    setActiveSection("hub");
  };

  const openSuministradores = () => {
    setActiveSection("suministradores");
  };

  const openRequisitosLegales = () => {
    setActiveSection("requisitos-legales");
  };

  const openObjetivos = () => {
    setActiveSection("objetivos");
  };

  const openDafo = () => {
    setActiveSection("dafo");
  };

  const openPlanificacionCambios = () => {
    setActiveSection("planificacion-de-cambios");
  };

  const openRiesgosOportunidades = () => {
    setActiveSection("riesgos-oportunidades");
  };

  const openMantenimientoVehiculos = () => setActiveSection("mantenimiento-vehiculos");
  const openMantenimientoInfraestructuras = () => setActiveSection("mantenimiento-infraestructuras");
  const openControlOperacional = () => setActiveSection("control-operacional");
  const openFormacion = () => setActiveSection("formacion");

  const openCreateModal = () => {
    setFormMode("create");
    setEditingIncidencia(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEditModal = (incidencia: CalidadIncidencia) => {
    setFormMode("edit");
    setEditingIncidencia(incidencia);
    setFormError(null);
    setFormOpen(true);
  };

  const closeFormModal = () => {
    setFormOpen(false);
    setFormError(null);
  };

  const handleSubmitIncidencia = async (
    values: CalidadIncidenciaFormValues,
  ) => {
    setFormSubmitting(true);
    setFormError(null);

    try {
      const payload = mapFormToIncidenciaPayload(values);

      if (formMode === "create") {
        await createCalidadIncidencia(payload);

        const targetPage = Math.max(
          1,
          Math.ceil((meta.total + 1) / Math.max(1, meta.per_page)),
        );

        closeFormModal();

        if (targetPage === page) {
          await reload();
        } else {
          setPage(targetPage);
        }

        return;
      } else if (editingIncidencia) {
        await updateCalidadIncidencia(editingIncidencia.id, payload);
      }

      closeFormModal();
      await reload();
    } catch (requestError) {
      setFormError(getRequestErrorMessage(requestError, "No se pudo guardar la incidencia."));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteIncidencia = async () => {
    if (!deleteTarget) {
      return;
    }

    setDeleteSubmitting(true);

    try {
      await deleteCalidadIncidencia(deleteTarget.id);
      setDeleteTarget(null);

      if (selectedIncidenciaId === deleteTarget.id) {
        setSelectedIncidenciaId(null);
        setActiveSection("incidencias");
      }

      const nextTotal = Math.max(0, meta.total - 1);
      const targetPage = Math.max(
        1,
        Math.min(page, Math.ceil(Math.max(1, nextTotal) / Math.max(1, meta.per_page))),
      );

      if (targetPage === page) {
        await reload();
      } else {
        setPage(targetPage);
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleSubmitControlModificacion = async (
    payload: CalidadControlModificacionPayload,
  ) => {
    if (!controlFormState.open) {
      return;
    }

    setControlFormSubmitting(true);
    setControlFormError(null);

    try {
      if (controlFormState.mode === "create") {
        await createCalidadIncidenciaControlModificacion(payload);
      } else if (controlFormState.row) {
        await updateCalidadIncidenciaControlModificacion(
          controlFormState.row.id,
          payload,
        );
      }

      setControlFormState({ open: false });
      await reload();
    } catch (requestError) {
      setControlFormError(
        getRequestErrorMessage(
          requestError,
          "No se pudo guardar la modificación del documento.",
        ),
      );
    } finally {
      setControlFormSubmitting(false);
    }
  };

  const handleDeleteControlModificacion = async () => {
    if (!controlDeleteState.open) {
      return;
    }

    setControlDeleteSubmitting(true);

    try {
      await deleteCalidadIncidenciaControlModificacion(controlDeleteState.id);
      setControlDeleteState({ open: false });
      await reload();
    } finally {
      setControlDeleteSubmitting(false);
    }
  };

  const openFicha = (incidencia: CalidadIncidencia) => {
    if (!incidencia.ncp) {
      return;
    }

    setSelectedIncidenciaId(incidencia.id);
    setFichaSaveError(null);
    setActiveSection("ficha");
  };

  const backToIncidencias = () => {
    setActiveSection("incidencias");
    setFichaSaveError(null);
  };

  const handleSaveFicha = async (draft: NonNullable<typeof ficha>) => {
    if (selectedIncidenciaId === null) {
      return;
    }

    setFichaSaving(true);
    setFichaSaveError(null);

    try {
      const response = await updateCalidadNcpFicha(
        selectedIncidenciaId,
        mapFichaToPayload(draft),
      );

      setFicha(response);
    } catch (requestError) {
      setFichaSaveError(
        getRequestErrorMessage(requestError, "No se pudo guardar la ficha NCp."),
      );
    } finally {
      setFichaSaving(false);
    }
  };

  const formInitialValues = useMemo(
    () =>
      formMode === "edit" && editingIncidencia
        ? mapIncidenciaToForm(editingIncidencia)
        : createEmptyCalidadIncidenciaForm(meta.next_numero),
    [editingIncidencia, formMode, meta.next_numero],
  );

  return (
    <div className="page-shell calidad-page-shell" data-certificaciones-readonly={canManageCertificaciones ? "false" : "true"}>
      {activeSection === "hub" ? (
        <CertificationStandardHero
          title="ISO 9001-14001"
          description="Gestiona el sistema integrado ISO 9001-14001 con sus registros, incidencias, no conformidades, acciones correctivas y documentación asociada para auditorías."
          secondaryDescription="Este estándar agrupa las secciones operativas de gestión ISO dentro de la estructura funcional de Certificaciones."
          stats={[
            { icon: ClipboardList, label: "Secciones", value: "14 activas" },
            { icon: FolderSearch, label: "Vista", value: ISO_9001_14001_SECTION_LABELS.hub },
            { icon: CheckCircle2, label: "Registros", value: "Listo" },
          ]}
          actions={
            <IsoCertificateActions
              standard="iso-9001-14001"
              slots={certificates.slots}
              loading={certificates.loading}
              canManage={certificates.canManage}
              variant="hero"
              onPreview={setPreviewCertificate}
              onUpload={(slot) => {
                certificates.clearError();
                setUploadCertificate(slot);
              }}
            />
          }
          onBack={() => void navigate({ to: "/certificaciones" })}
        />
      ) : null}

      <div className="calidad-workspace-grid">
        {activeSection === "hub" ? (
          <Iso900114001Hub
            onOpenManual={openManual}
            onOpenIndicadores={openIndicadores}
            onOpenBiblioteca={() => void navigate({ to: "/certificaciones/iso-9001-14001/biblioteca" })}
            onOpenDafo={openDafo}
            onOpenIncidencias={openIncidencias}
            onOpenSuministradores={openSuministradores}
            onOpenRequisitosLegales={openRequisitosLegales}
            onOpenObjetivos={openObjetivos}
            onOpenPlanificacionCambios={openPlanificacionCambios}
            onOpenRiesgosOportunidades={openRiesgosOportunidades}
            onOpenMantenimientoVehiculos={openMantenimientoVehiculos}
            onOpenMantenimientoInfraestructuras={openMantenimientoInfraestructuras}
            onOpenControlOperacional={openControlOperacional}
            onOpenFormacion={openFormacion}
          />
        ) : activeSection === "manual-sistema-integrado" ? (
          <ManualSistemaIntegradoSection standard="iso-9001-14001" onBack={backToHub} />
        ) : activeSection === "indicadores" ? (
          <IndicadoresSection standard="iso-9001-14001" onBack={backToHub} />
        ) : activeSection === "incidencias" ? (
          <section className="calidad-section">
            <Iso900114001SectionHeader
              eyebrow="Consulta histórica"
              title="Incidencias"
              description="Registro histórico con creación, edición, eliminación y acceso a la ficha documental NCp."
              onBack={backToHub}
              onRefresh={() => {
                void reload();
              }}
              refreshing={loading}
            />

            <div className="iso9001-overview-grid">
              <Iso900114001OverviewCard
                icon={ClipboardList}
                label="Incidencias"
                value={loading ? "..." : meta.total}
              />
              <Iso900114001OverviewCard
                icon={FolderSearch}
                label="Registros visibles"
                value={loading ? "..." : incidencias.length}
              />
              <Iso900114001OverviewCard
                icon={CheckCircle2}
                label="Control documental"
                value={loading ? "..." : controlModificaciones.length}
              />
            </div>

            <Iso900114001Tabs
              tabs={[
                { id: "incidencias", label: "Incidencias", count: meta.total },
                {
                  id: "control",
                  label: "Control documental",
                  count: controlModificaciones.length,
                },
              ]}
              activeTab={incidenciasTab}
              onChange={setIncidenciasTab}
              ariaLabel="Tablas de incidencias"
            />

            {incidenciasTab === "control" ? (
              <CalidadControlModificacionesTable
                rows={controlModificaciones}
                loading={loading}
                error={error}
                onRetry={() => {
                  void reload();
                }}
                onCreate={() => {
                  setControlFormError(null);
                  setControlFormState({ open: true, mode: "create", row: null });
                }}
                onEdit={(row) => {
                  setControlFormError(null);
                  setControlFormState({ open: true, mode: "edit", row });
                }}
                onDelete={(row) =>
                  setControlDeleteState({
                    open: true,
                    id: row.id,
                    title: "Eliminar modificación",
                    description: `Se eliminará la modificación registrada en ${row.fecha}.`,
                  })
                }
              />
            ) : (
              <CalidadIncidenciasTable
                incidencias={incidencias}
                total={meta.total}
                currentPage={meta.current_page}
                lastPage={meta.last_page}
                from={meta.from}
                to={meta.to}
                loading={loading}
                error={error}
                filters={filters}
                onFiltersChange={(patch) => {
                  setPage(1);
                  setFilters((current) => ({ ...current, ...patch }));
                }}
                onRetry={() => {
                  void reload();
                }}
                onResetFilters={() => {
                  setPage(1);
                  setFilters(defaultCalidadFilters);
                }}
                onPageChange={setPage}
                onCreate={openCreateModal}
                onEdit={openEditModal}
                onDelete={(incidencia) => setDeleteTarget(incidencia)}
                onOpenFicha={openFicha}
              />
            )}
          </section>
        ) : activeSection === "suministradores" ? (
          <CalidadSuministradoresSection onBack={backToHub} />
        ) : activeSection === "requisitos-legales" ? (
          <CalidadRequisitosLegalesSection onBack={backToHub} />
        ) : activeSection === "objetivos" ? (
          <CalidadObjetivosSection onBack={backToHub} />
        ) : activeSection === "planificacion-de-cambios" ? (
          <CalidadPlanificacionCambiosSection onBack={backToHub} />
        ) : activeSection === "riesgos-oportunidades" ? (
          <CalidadRiesgosOportunidadesSection onBack={backToHub} />
        ) : activeSection === "dafo" ? (
          <CalidadDafoSection onBack={backToHub} />
        ) : activeSection === "mantenimiento-vehiculos" ? (
          <CalidadMantenimientoVehiculosSection onBack={backToHub} />
        ) : activeSection === "mantenimiento-infraestructuras" ? (
          <CalidadMantenimientoInfraestructurasSection onBack={backToHub} />
        ) : activeSection === "control-operacional" ? (
          <CalidadControlOperacionalSection onBack={backToHub} />
        ) : activeSection === "formacion" ? (
          <CalidadFormacionSection onBack={backToHub} />
        ) : (
          <CalidadNcpFichaView
            ficha={ficha}
            loading={fichaLoading}
            saving={fichaSaving}
            error={fichaError}
            saveError={fichaSaveError}
            onBack={backToIncidencias}
            onRetry={() => {
              void reloadFicha();
            }}
            onSave={handleSaveFicha}
          />
        )}
      </div>

      <CalidadIncidenciaFormModal
        open={formOpen}
        mode={formMode}
        initialValues={formInitialValues}
        submitting={formSubmitting}
        error={formError}
        onClose={closeFormModal}
        onSubmit={handleSubmitIncidencia}
      />

      <CalidadControlModificacionFormModal
        open={controlFormState.open}
        mode={controlFormState.open ? controlFormState.mode : "create"}
        initialValues={controlFormState.open ? controlFormState.row : null}
        submitting={controlFormSubmitting}
        error={controlFormError}
        onClose={() => setControlFormState({ open: false })}
        onSubmit={handleSubmitControlModificacion}
      />

      <CalidadConfirmDeleteModal
        open={deleteTarget !== null}
        numero={deleteTarget?.numero ?? null}
        deleting={deleteSubmitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteIncidencia}
      />

      <CalidadControlModificacionDeleteModal
        open={controlDeleteState.open}
        title={controlDeleteState.open ? controlDeleteState.title : ""}
        description={controlDeleteState.open ? controlDeleteState.description : ""}
        deleting={controlDeleteSubmitting}
        onClose={() => setControlDeleteState({ open: false })}
        onConfirm={handleDeleteControlModificacion}
      />

      <IsoCertificatePreviewModal
        slot={previewCertificate}
        onClose={() => setPreviewCertificate(null)}
      />

      <IsoCertificateUploadModal
        slot={uploadCertificate}
        uploadingCode={certificates.uploadingCode}
        error={certificates.error}
        onClose={() => {
          certificates.clearError();
          setUploadCertificate(null);
        }}
        onSubmit={async (slot, file) => {
          const saved = await certificates.upload(slot.codigo, file);
          if (saved) {
            setUploadCertificate(null);
          }
        }}
      />
    </div>
  );
}

function getRequestErrorMessage(error: unknown, fallback: string) {
  if (!isAxiosError(error)) {
    return fallback;
  }

  const fieldErrors = error.response?.data?.errors;
  const firstError =
    fieldErrors && typeof fieldErrors === "object"
      ? Object.values(fieldErrors).find(Array.isArray)?.[0]
      : null;

  if (typeof firstError === "string") {
    return firstError;
  }

  const message = error.response?.data?.message;

  return typeof message === "string" ? message : fallback;
}
